import { createTheme } from "@mui/material/styles";

const theme = createTheme({
  palette: {
    primary: {
      main: "#0e86d4",
      light: "#3da2e2",
      dark: "#0a6baa",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#B3CDDC",
      light: "#d4e4ed",
      dark: "#8bafc4",
    },
    background: {
      default: "#f0f5f9",
      paper: "rgba(255, 255, 255, 0.72)",
    },
    text: {
      primary: "#1a2332",
      secondary: "#5a6d80",
    },
    error: {
      main: "#e53e3e",
      light: "#fff5f5",
    },
    success: {
      main: "#2ecc71",
      light: "#f0fdf4",
    },
  },
  typography: {
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif",
    h4: {
      fontWeight: 700,
      letterSpacing: "-0.025em",
    },
    h5: {
      fontWeight: 700,
      letterSpacing: "-0.02em",
    },
    h6: {
      fontWeight: 600,
      letterSpacing: "-0.015em",
    },
    body1: {
      lineHeight: 1.6,
    },
    body2: {
      lineHeight: 1.5,
    },
    button: {
      textTransform: "none" as const,
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: "10px 24px",
          fontSize: "0.938rem",
          boxShadow: "none",
          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            boxShadow: "none",
            transform: "translateY(-1px)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
        },
        contained: {
          background: "linear-gradient(135deg, #0e86d4 0%, #1a9be6 50%, #0e86d4 100%)",
          backgroundSize: "200% 200%",
          boxShadow: "0 2px 12px rgba(14, 134, 212, 0.25)",
          "&:hover": {
            background: "linear-gradient(135deg, #0a6baa 0%, #0e86d4 50%, #0a6baa 100%)",
            boxShadow: "0 4px 20px rgba(14, 134, 212, 0.35)",
          },
        },
        outlined: {
          borderColor: "rgba(14, 134, 212, 0.2)",
          color: "#1a2332",
          backdropFilter: "blur(8px)",
          backgroundColor: "rgba(255, 255, 255, 0.5)",
          "&:hover": {
            borderColor: "#0e86d4",
            backgroundColor: "rgba(14, 134, 212, 0.04)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          backgroundColor: "rgba(255, 255, 255, 0.72)",
          backdropFilter: "blur(20px) saturate(180%)",
          WebkitBackdropFilter: "blur(20px) saturate(180%)",
          border: "1px solid rgba(255, 255, 255, 0.5)",
          boxShadow:
            "0 2px 8px rgba(0, 0, 0, 0.04), 0 8px 32px rgba(0, 0, 0, 0.06)",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 12,
            backgroundColor: "rgba(255, 255, 255, 0.6)",
            backdropFilter: "blur(8px)",
            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.8)",
            },
            "&.Mui-focused": {
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              boxShadow: "0 0 0 3px rgba(14, 134, 212, 0.12)",
            },
            "& fieldset": {
              borderColor: "rgba(14, 134, 212, 0.15)",
              transition: "border-color 0.25s ease",
            },
            "&:hover fieldset": {
              borderColor: "rgba(14, 134, 212, 0.35)",
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 8px rgba(0, 0, 0, 0.06)",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardError: {
          borderRadius: 12,
          backgroundColor: "rgba(229, 62, 62, 0.06)",
          backdropFilter: "blur(8px)",
          border: "1px solid rgba(229, 62, 62, 0.15)",
        },
        standardSuccess: {
          borderRadius: 12,
          backgroundColor: "rgba(46, 204, 113, 0.06)",
          backdropFilter: "blur(8px)",
          border: "1px solid rgba(46, 204, 113, 0.15)",
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          border: "2px solid rgba(255, 255, 255, 0.4)",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          backgroundColor: "rgba(255, 255, 255, 0.82)",
          backdropFilter: "blur(20px) saturate(180%)",
          WebkitBackdropFilter: "blur(20px) saturate(180%)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          border: "1px solid rgba(255, 255, 255, 0.5)",
          marginTop: 8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          margin: "2px 8px",
          padding: "8px 12px",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: "rgba(14, 134, 212, 0.06)",
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "rgba(14, 134, 212, 0.08)",
        },
      },
    },
  },
});

export default theme;
